/*
import React, { useState, useEffect } from 'react';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const API_URL = `${process.env.REACT_APP_API_URL}/api/lease-to-own`;
const VEHICLES_API = 'http://localhost:5000/api/vehicles';

function LeaseToOwnList() {
  const [leaseVehicles, setLeaseVehicles] = useState([]);
  const [originalVehicles, setOriginalVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCarForm, setShowCarForm] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);

  // Car Form state
  const [carFormData, setCarFormData] = useState({
    carName: '',
    sellingPrice: '',
    originalVehicleId: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [brochureFile, setBrochureFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [useExistingCar, setUseExistingCar] = useState(true);

  // Plan Form state
  const [planFormData, setPlanFormData] = useState({
    optionTitle: '',
    option1Name: '',
    option1Value: '',
    option2Name: '',
    option2Value: '',
    option3Name: '',
    option3Value: ''
  });

  const fetchData = async () => {
    try {
      const [leaseRes, vehiclesRes] = await Promise.all([
        fetch(API_URL),
        fetch(VEHICLES_API)
      ]);

      const leaseData = await leaseRes.json();
      const vehiclesData = await vehiclesRes.json();

      if (leaseRes.ok) setLeaseVehicles(leaseData.data || []);
      if (vehiclesRes.ok) setOriginalVehicles(vehiclesData.data || []);
    } catch (err) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // CAR FORM HANDLERS
  const handleCarFormChange = (e) => {
    setCarFormData({ ...carFormData, [e.target.name]: e.target.value });
  };

  const handleExistingCarChange = (e) => {
    const selectedId = e.target.value;
    setCarFormData({ ...carFormData, originalVehicleId: selectedId });

    if (selectedId) {
      const vehicle = originalVehicles.find(v => v._id === selectedId);
      if (vehicle) {
        setCarFormData(prev => ({ ...prev, carName: vehicle.name }));
        setImagePreview(vehicle.imageURL);
        setImageFile(null);
      }
    } else {
      setCarFormData(prev => ({ ...prev, carName: '', originalVehicleId: '' }));
      setImagePreview('');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleBrochureChange = (e) => {
    const file = e.target.files[0];
    if (file) setBrochureFile(file);
  };

  const handleCarSubmit = async (e) => {
    e.preventDefault();
    if (!imagePreview) {
      setFormMessage('Please provide an image');
      return;
    }

    setFormLoading(true);
    setFormMessage('');

    try {
      let imageURL = imagePreview;
      let brochureURL = null;

      // Upload new image if file is selected
      if (imageFile) {
        const imageRef = ref(storage, `lease-to-own/images/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imageURL = await getDownloadURL(imageRef);
      }

      // Upload brochure if provided
      if (brochureFile) {
        const brochureRef = ref(storage, `lease-to-own/brochures/${Date.now()}_${brochureFile.name}`);
        await uploadBytes(brochureRef, brochureFile);
        brochureURL = await getDownloadURL(brochureRef);
      }

      const payload = {
        carName: carFormData.carName.trim(),
        sellingPrice: carFormData.sellingPrice ? Number(carFormData.sellingPrice) : null,
        imageURL,
        brochureURL,
        originalVehicleId: carFormData.originalVehicleId || null
      };

      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to save');
      }

      setFormMessage('✅ Car added successfully!');
      resetCarForm();
      fetchData();
    } catch (err) {
      setFormMessage('❌ ' + err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const resetCarForm = () => {
    setCarFormData({
      carName: '',
      sellingPrice: '',
      originalVehicleId: ''
    });
    setImageFile(null);
    setBrochureFile(null);
    setImagePreview('');
    setUseExistingCar(true);
    setShowCarForm(false);
  };

  // PLAN FORM HANDLERS
  const handlePlanFormChange = (e) => {
    setPlanFormData({ ...planFormData, [e.target.name]: e.target.value });
  };

  const openPlanForm = (car) => {
    setSelectedCar(car);
    setPlanFormData({
      optionTitle: '',
      option1Name: '',
      option1Value: '',
      option2Name: '',
      option2Value: '',
      option3Name: '',
      option3Value: ''
    });
    setShowPlanForm(true);
  };

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        optionTitle: planFormData.optionTitle.trim(),
        option1Name: planFormData.option1Name.trim(),
        option1Value: planFormData.option1Value.trim(),
        option2Name: planFormData.option2Name.trim(),
        option2Value: planFormData.option2Value.trim(),
        option3Name: planFormData.option3Name.trim(),
        option3Value: planFormData.option3Value.trim()
      };

      const res = await fetch(`${API_URL}/${selectedCar._id}/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to add plan');
      }

      alert('✅ Plan added successfully!');
      setShowPlanForm(false);
      fetchData();
    } catch (err) {
      alert('❌ ' + err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // EDIT PLAN
  const openEditPlanModal = (car, plan) => {
    setSelectedCar(car);
    setEditingPlan(plan);
    setPlanFormData({
      optionTitle: plan.optionTitle,
      option1Name: plan.option1Name,
      option1Value: plan.option1Value,
      option2Name: plan.option2Name,
      option2Value: plan.option2Value,
      option3Name: plan.option3Name,
      option3Value: plan.option3Value
    });
    setShowEditPlanModal(true);
  };

  const handleEditPlanSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        optionTitle: planFormData.optionTitle.trim(),
        option1Name: planFormData.option1Name.trim(),
        option1Value: planFormData.option1Value.trim(),
        option2Name: planFormData.option2Name.trim(),
        option2Value: planFormData.option2Value.trim(),
        option3Name: planFormData.option3Name.trim(),
        option3Value: planFormData.option3Value.trim()
      };

      const res = await fetch(`${API_URL}/${selectedCar._id}/plan/${editingPlan._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to update plan');
      }

      alert('✅ Plan updated successfully!');
      setShowEditPlanModal(false);
      fetchData();
    } catch (err) {
      alert('❌ ' + err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // DELETE PLAN
  const deletePlan = async (carId, planId) => {
    if (!window.confirm('Delete this payment plan?')) return;

    try {
      const res = await fetch(`${API_URL}/${carId}/plan/${planId}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete plan');

      alert('✅ Plan deleted successfully');
      fetchData();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // DELETE CAR
  const handleDeleteCar = async (car) => {
    if (!window.confirm('Delete this car and all its payment plans?')) return;

    try {
      if (car.imageURL && !car.originalVehicleId) {
        try {
          await deleteObject(ref(storage, car.imageURL));
        } catch (err) {
          console.warn('Image delete failed');
        }
      }

      if (car.brochureURL) {
        try {
          await deleteObject(ref(storage, car.brochureURL));
        } catch (err) {
          console.warn('Brochure delete failed');
        }
      }

      await fetch(`${API_URL}/${car._id}`, { method: 'DELETE' });
      fetchData();
      alert('✅ Car deleted successfully');
    } catch (err) {
      alert('❌ Error: ' + err.message);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return Number(price).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>
        Loading lease-to-own vehicles...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', color: '#333', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ padding: '4rem 1rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h1 style={{ fontSize: '3.5rem', fontWeight: '300', color: '#222' }}>
            Manage <span style={{ fontWeight: '600', color: '#2c5282' }}>Lease-to-Own</span>
          </h1>
        </div>
<div style={{ 
  textAlign: 'center', 
  marginBottom: 'clamp(2rem, 5vw, 4rem)' 
}}>
  <div style={{ 
    display: 'flex', 
    gap: 'clamp(1rem, 2vw, 1.5rem)', 
    justifyContent: 'center', 
    flexWrap: 'wrap',
    padding: '0 1rem'
  }}>
    <button
      onClick={() => { resetCarForm(); setShowCarForm(true); }}
      style={{
        background: '#2c5282',
        color: 'white',
        padding: 'clamp(0.8rem, 2vw, 1rem) clamp(1.5rem, 4vw, 3rem)',
        border: 'none',
        borderRadius: '8px',
        fontSize: 'clamp(0.95rem, 2vw, 1.2rem)',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(44,82,130,0.3)',
        transition: 'all 0.3s ease',
        minWidth: 'clamp(180px, 40vw, 220px)'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-3px)';
        e.target.style.boxShadow = '0 6px 20px rgba(44,82,130,0.4)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 4px 15px rgba(44,82,130,0.3)';
      }}
    >
      ➕ Add New Car
    </button>
    
    <button
      onClick={() => window.location.href = '/admin-lease-to-own-documents'}
      style={{
        background: '#2c5282',
        color: 'white',
        padding: 'clamp(0.8rem, 2vw, 1rem) clamp(1.5rem, 4vw, 3rem)',
        border: 'none',
        borderRadius: '8px',
        fontSize: 'clamp(0.95rem, 2vw, 1.2rem)',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(44,82,130,0.3)',
        transition: 'all 0.3s ease',
        minWidth: 'clamp(180px, 40vw, 220px)'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-3px)';
        e.target.style.boxShadow = '0 6px 20px rgba(44,82,130,0.4)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 4px 15px rgba(44,82,130,0.3)';
      }}
    >
      📋 Add Notes + Agreement
    </button>
  </div>
</div>
        {showCarForm && (
          <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', marginBottom: '4rem', maxWidth: '800px', margin: '0 auto 4rem' }}>
            <h2 style={{ textAlign: 'center', color: '#2c5282', marginBottom: '2rem', fontSize: '2rem' }}>
              Add New Car
            </h2>

            {formMessage && (
              <div style={{
                padding: '1rem',
                marginBottom: '1.5rem',
                borderRadius: '8px',
                background: formMessage.startsWith('✅') ? '#d4edda' : '#f8d7da',
                color: formMessage.startsWith('✅') ? '#155724' : '#721c24',
                textAlign: 'center'
              }}>
                {formMessage}
              </div>
            )}

            <form onSubmit={handleCarSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mode</label>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <label>
                    <input type="radio" checked={useExistingCar} onChange={() => setUseExistingCar(true)} />
                    Select from Catalog
                  </label>
                  <label>
                    <input type="radio" checked={!useExistingCar} onChange={() => setUseExistingCar(false)} />
                    Create New
                  </label>
                </div>
              </div>

              {useExistingCar ? (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Select Vehicle from Catalog *</label>
                  <select
                    value={carFormData.originalVehicleId}
                    onChange={handleExistingCarChange}
                    required
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                  >
                    <option value="">-- Choose a vehicle --</option>
                    {originalVehicles.map(v => (
                      <option key={v._id} value={v._id}>
                        {v.name} ({v.status})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Car Name *</label>
                    <input
                      type="text"
                      name="carName"
                      value={carFormData.carName}
                      onChange={handleCarFormChange}
                      required
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Image *</label>
                    <input type="file" accept="image/*" onChange={handleImageChange} required />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Brochure (PDF - Optional)</label>
                    <input type="file" accept=".pdf" onChange={handleBrochureChange} />
                  </div>
                </>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Selling Price (Nu.) - Optional</label>
                <input
                  type="number"
                  name="sellingPrice"
                  value={carFormData.sellingPrice}
                  onChange={handleCarFormChange}
                  placeholder="e.g. 5000000"
                  style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>Leave blank if not applicable</small>
              </div>

              {imagePreview && (
                <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '1rem 3rem',
                    background: '#2c5282',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    cursor: formLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {formLoading ? 'Saving...' : 'Add Car'}
                </button>
                <button
                  type="button"
                  onClick={resetCarForm}
                  style={{
                    padding: '1rem 3rem',
                    background: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {leaseVehicles.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '2rem' }}>
            {leaseVehicles.map((car) => (
              <div
                key={car._id}
                onMouseEnter={() => setHoveredCard(car._id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: hoveredCard === car._id ? '0 20px 40px rgba(0,0,0,0.15)' : '0 8px 25px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s ease',
                  transform: hoveredCard === car._id ? 'translateY(-10px)' : 'translateY(0)'
                }}
              >
                <div style={{ height: '280px', overflow: 'hidden' }}>
                  <img
                    src={car.imageURL}
                    alt={car.carName}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s ease',
                      transform: hoveredCard === car._id ? 'scale(1.1)' : 'scale(1)'
                    }}
                  />
                </div>

                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.6rem', fontWeight: '600', marginBottom: '0.5rem' }}>{car.carName}</h3>
                  
                  {car.sellingPrice && (
                    <p style={{ color: '#2c5282', fontWeight: '600', fontSize: '1.2rem', marginBottom: '1rem' }}>
                      Nu. {formatPrice(car.sellingPrice)}
                    </p>
                  )}

                  {car.brochureURL && (
                    <a 
                      href={car.brochureURL} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ 
                        display: 'inline-block',
                        marginBottom: '1rem',
                        color: '#2c5282',
                        textDecoration: 'underline'
                      }}
                    >
                      📄 View Brochure
                    </a>
                  )}

                  <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                    <h4 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                      Payment Plans ({car.paymentPlans?.length || 0})
                    </h4>

                    {car.paymentPlans && car.paymentPlans.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {car.paymentPlans.map((plan) => (
                          <div key={plan._id} style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #ddd' }}>
                            <div style={{ marginBottom: '0.8rem' }}>
                              <strong style={{ fontSize: '1.1rem', color: '#2c5282' }}>{plan.optionTitle}</strong>
                            </div>

                            <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: '1.6', marginBottom: '0.8rem' }}>
                              {plan.option1Name && <p><strong>{plan.option1Name}:</strong> {plan.option1Value}</p>}
                              {plan.option2Name && <p><strong>{plan.option2Name}:</strong> {plan.option2Value}</p>}
                              {plan.option3Name && <p><strong>{plan.option3Name}:</strong> {plan.option3Value}</p>}
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button 
                                onClick={() => openEditPlanModal(car, plan)}
                                style={{ 
                                  flex: 1, 
                                  padding: '0.5rem', 
                                  background: '#3182ce', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => deletePlan(car._id, plan._id)}
                                style={{ 
                                  flex: 1, 
                                  padding: '0.5rem', 
                                  background: '#e53e3e', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                        No payment plans yet
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginTop: '1.5rem' }}>
                    <button 
                      onClick={() => openPlanForm(car)} 
                      style={{ 
                        padding: '0.8rem', 
                        background: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      + Create Plan
                    </button>
                    <button 
                      onClick={() => handleDeleteCar(car)} 
                      style={{ 
                        padding: '0.8rem', 
                        background: '#e53e3e', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Delete Car
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {leaseVehicles.length === 0 && !showCarForm && (
          <div style={{ textAlign: 'center', padding: '8rem 2rem', color: '#888' }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#444' }}>No cars added yet</h2>
            <p style={{ fontSize: '1.3rem' }}>Click the button above to add your first lease-to-own car</p>
          </div>
        )}

        {showPlanForm && selectedCar && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#2c5282', textAlign: 'center', fontSize: '1.8rem' }}>
                Create Payment Plan for {selectedCar.carName}
              </h3>

              <form onSubmit={handlePlanSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option Title *</label>
                  <input 
                    type="text" 
                    name="optionTitle" 
                    value={planFormData.optionTitle} 
                    onChange={handlePlanFormChange} 
                    required 
                    placeholder="e.g. 36 Months Plan"
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Name</label>
                    <input 
                      type="text" 
                      name="option1Name" 
                      value={planFormData.option1Name} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Down Payment"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Value</label>
                    <input 
                      type="text" 
                      name="option1Value" 
                      value={planFormData.option1Value} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Nu. 500,000"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Name</label>
                    <input 
                      type="text" 
                      name="option2Name" 
                      value={planFormData.option2Name} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Monthly Payment"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Value</label>
                    <input 
                      type="text" 
                      name="option2Value" 
                      value={planFormData.option2Value} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Nu. 50,000"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Name</label>
                    <input 
                      type="text" 
                      name="option3Name" 
                      value={planFormData.option3Name} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Tenure"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Value</label>
                    <input 
                      type="text" 
                      name="option3Value" 
                      value={planFormData.option3Value} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. 36 months"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button 
                    type="submit" 
                    disabled={formLoading}
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#2c5282', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: formLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {formLoading ? 'Saving...' : 'Add Plan'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowPlanForm(false)} 
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#777', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditPlanModal && editingPlan && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#2c5282', textAlign: 'center', fontSize: '1.8rem' }}>
                Edit Payment Plan
              </h3>

              <form onSubmit={handleEditPlanSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option Title *</label>
                  <input 
                    type="text" 
                    name="optionTitle" 
                    value={planFormData.optionTitle} 
                    onChange={handlePlanFormChange} 
                    required 
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Name</label>
                    <input 
                      type="text" 
                      name="option1Name" 
                      value={planFormData.option1Name} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Value</label>
                    <input 
                      type="text" 
                      name="option1Value" 
                      value={planFormData.option1Value} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Name</label>
                    <input 
                      type="text" 
                      name="option2Name" 
                      value={planFormData.option2Name} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Value</label>
                    <input 
                      type="text" 
                      name="option2Value" 
                      value={planFormData.option2Value} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Name</label>
                    <input 
                      type="text" 
                      name="option3Name" 
                      value={planFormData.option3Name} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Value</label>
                    <input 
                      type="text" 
                      name="option3Value" 
                      value={planFormData.option3Value} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button 
                    type="submit" 
                    disabled={formLoading}
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#2c5282', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: formLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {formLoading ? 'Saving...' : 'Update Plan'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowEditPlanModal(false)} 
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#777', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeaseToOwnList;*/

import React, { useState, useEffect } from 'react';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Proxy-relative in dev, full URL in production
const LEASE_API = process.env.NODE_ENV === 'production'
  ? `${process.env.REACT_APP_API_URL}/api/lease-to-own` // ← your real backend URL
  : '/api/lease-to-own';

const VEHICLES_API = process.env.NODE_ENV === 'production'
  ? `${process.env.REACT_APP_API_URL}/api/vehicles`
  : '/api/vehicles';

function LeaseToOwnList() {
  const [leaseVehicles, setLeaseVehicles] = useState([]);
  const [originalVehicles, setOriginalVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Added missing error state
  const [showCarForm, setShowCarForm] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);

  // Car Form state
  const [carFormData, setCarFormData] = useState({
    carName: '',
    sellingPrice: '',
    originalVehicleId: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [brochureFile, setBrochureFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [useExistingCar, setUseExistingCar] = useState(true);

  // Plan Form state
  const [planFormData, setPlanFormData] = useState({
    optionTitle: '',
    option1Name: '',
    option1Value: '',
    option2Name: '',
    option2Value: '',
    option3Name: '',
    option3Value: ''
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const [leaseRes, vehiclesRes] = await Promise.all([
        fetch(LEASE_API, {
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
          },
        }),
        fetch(VEHICLES_API, {
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
          },
        })
      ]);

      clearTimeout(timeoutId);

      if (!leaseRes.ok) throw new Error(`Lease fetch failed: ${leaseRes.status}`);
      if (!vehiclesRes.ok) throw new Error(`Vehicles fetch failed: ${vehiclesRes.status}`);

      const leaseData = await leaseRes.json();
      const vehiclesData = await vehiclesRes.json();

      setLeaseVehicles(leaseData.data || []);
      setOriginalVehicles(vehiclesData.data || []);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load lease-to-own data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // CAR FORM HANDLERS
  const handleCarFormChange = (e) => {
    setCarFormData({ ...carFormData, [e.target.name]: e.target.value });
  };

  const handleExistingCarChange = (e) => {
    const selectedId = e.target.value;
    setCarFormData({ ...carFormData, originalVehicleId: selectedId });

    if (selectedId) {
      const vehicle = originalVehicles.find(v => v._id === selectedId);
      if (vehicle) {
        setCarFormData(prev => ({ ...prev, carName: vehicle.name }));
        setImagePreview(vehicle.imageURL);
        setImageFile(null);
      }
    } else {
      setCarFormData(prev => ({ ...prev, carName: '', originalVehicleId: '' }));
      setImagePreview('');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleBrochureChange = (e) => {
    const file = e.target.files[0];
    if (file) setBrochureFile(file);
  };

  const handleCarSubmit = async (e) => {
    e.preventDefault();
    if (!imagePreview) {
      setFormMessage('Please provide an image');
      return;
    }

    setFormLoading(true);
    setFormMessage('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // longer for uploads

    try {
      let imageURL = imagePreview;
      let brochureURL = null;

      if (imageFile) {
        const imageRef = ref(storage, `lease-to-own/images/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imageURL = await getDownloadURL(imageRef);
      }

      if (brochureFile) {
        const brochureRef = ref(storage, `lease-to-own/brochures/${Date.now()}_${brochureFile.name}`);
        await uploadBytes(brochureRef, brochureFile);
        brochureURL = await getDownloadURL(brochureRef);
      }

      const payload = {
        carName: carFormData.carName.trim(),
        sellingPrice: carFormData.sellingPrice ? Number(carFormData.sellingPrice) : null,
        imageURL,
        brochureURL,
        originalVehicleId: carFormData.originalVehicleId || null
      };

      const res = await fetch(LEASE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add car');
      }

      setFormMessage('✅ Car added successfully!');
      resetCarForm();
      fetchData();
    } catch (err) {
      console.error('Car submit error:', err);
      setFormMessage('❌ ' + (err.message || 'Failed to save car'));
    } finally {
      setFormLoading(false);
    }
  };

  const resetCarForm = () => {
    setCarFormData({
      carName: '',
      sellingPrice: '',
      originalVehicleId: ''
    });
    setImageFile(null);
    setBrochureFile(null);
    setImagePreview('');
    setUseExistingCar(true);
    setShowCarForm(false);
  };

  // PLAN FORM HANDLERS
  const handlePlanFormChange = (e) => {
    setPlanFormData({ ...planFormData, [e.target.name]: e.target.value });
  };

  const openPlanForm = (car) => {
    setSelectedCar(car);
    setPlanFormData({
      optionTitle: '',
      option1Name: '',
      option1Value: '',
      option2Name: '',
      option2Value: '',
      option3Name: '',
      option3Value: ''
    });
    setShowPlanForm(true);
  };

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const payload = {
        optionTitle: planFormData.optionTitle.trim(),
        option1Name: planFormData.option1Name.trim(),
        option1Value: planFormData.option1Value.trim(),
        option2Name: planFormData.option2Name.trim(),
        option2Value: planFormData.option2Value.trim(),
        option3Name: planFormData.option3Name.trim(),
        option3Value: planFormData.option3Value.trim()
      };

      const res = await fetch(`${LEASE_API}/${selectedCar._id}/plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add plan');
      }

      setFormMessage('✅ Plan added successfully!');
      setShowPlanForm(false);
      fetchData();
    } catch (err) {
      console.error('Plan submit error:', err);
      setFormMessage('❌ ' + (err.message || 'Failed to add plan'));
    } finally {
      setFormLoading(false);
    }
  };

  // EDIT PLAN
  const openEditPlanModal = (car, plan) => {
    setSelectedCar(car);
    setEditingPlan(plan);
    setPlanFormData({
      optionTitle: plan.optionTitle,
      option1Name: plan.option1Name,
      option1Value: plan.option1Value,
      option2Name: plan.option2Name,
      option2Value: plan.option2Value,
      option3Name: plan.option3Name,
      option3Value: plan.option3Value
    });
    setShowEditPlanModal(true);
  };

  const handleEditPlanSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const payload = {
        optionTitle: planFormData.optionTitle.trim(),
        option1Name: planFormData.option1Name.trim(),
        option1Value: planFormData.option1Value.trim(),
        option2Name: planFormData.option2Name.trim(),
        option2Value: planFormData.option2Value.trim(),
        option3Name: planFormData.option3Name.trim(),
        option3Value: planFormData.option3Value.trim()
      };

      const res = await fetch(`${LEASE_API}/${selectedCar._id}/plan/${editingPlan._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Failed to update plan');

      setFormMessage('✅ Plan updated successfully!');
      setShowEditPlanModal(false);
      fetchData();
    } catch (err) {
      console.error('Plan update error:', err);
      setFormMessage('❌ ' + (err.message || 'Failed to update plan'));
    } finally {
      setFormLoading(false);
    }
  };

  // DELETE PLAN
  const deletePlan = async (carId, planId) => {
    if (!window.confirm('Delete this payment plan?')) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const res = await fetch(`${LEASE_API}/${carId}/plan/${planId}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Failed to delete plan');

      setFormMessage('✅ Plan deleted');
      fetchData();
    } catch (err) {
      console.error('Plan delete error:', err);
      setFormMessage('❌ ' + (err.message || 'Failed to delete plan'));
    }
  };

  // DELETE CAR
  const handleDeleteCar = async (car) => {
    if (!window.confirm('Delete this car and all its payment plans?')) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      if (car.imageURL && !car.originalVehicleId) {
        await deleteObject(ref(storage, car.imageURL)).catch(err => {
          if (err.code !== 'storage/object-not-found') throw err;
        });
      }

      if (car.brochureURL) {
        await deleteObject(ref(storage, car.brochureURL)).catch(err => {
          if (err.code !== 'storage/object-not-found') throw err;
        });
      }

      const res = await fetch(`${LEASE_API}/${car._id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Failed to delete car');

      setFormMessage('✅ Car deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Car delete error:', err);
      setFormMessage('❌ ' + (err.message || 'Failed to delete car'));
    }
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return Number(price).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.8rem',
        background: '#f8f9fa',
        color: '#333'
      }}>
        Loading lease-to-own vehicles...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        color: '#dc3545',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Error Loading Data</h2>
        <p style={{ fontSize: '1.3rem', maxWidth: '600px' }}>{error}</p>
        <button
          onClick={fetchData}
          style={{
            marginTop: '2rem',
            padding: '1rem 2.5rem',
            background: '#2c5282',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1.1rem',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', color: '#333', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ padding: '4rem 1rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h1 style={{ fontSize: '3.5rem', fontWeight: '300', color: '#222' }}>
            Manage <span style={{ fontWeight: '600', color: '#2c5282' }}>Lease-to-Own</span>
          </h1>
        </div>
<div style={{ 
  textAlign: 'center', 
  marginBottom: 'clamp(2rem, 5vw, 4rem)' 
}}>
  <div style={{ 
    display: 'flex', 
    gap: 'clamp(1rem, 2vw, 1.5rem)', 
    justifyContent: 'center', 
    flexWrap: 'wrap',
    padding: '0 1rem'
  }}>
    <button
      onClick={() => { resetCarForm(); setShowCarForm(true); }}
      style={{
        background: '#2c5282',
        color: 'white',
        padding: 'clamp(0.8rem, 2vw, 1rem) clamp(1.5rem, 4vw, 3rem)',
        border: 'none',
        borderRadius: '8px',
        fontSize: 'clamp(0.95rem, 2vw, 1.2rem)',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(44,82,130,0.3)',
        transition: 'all 0.3s ease',
        minWidth: 'clamp(180px, 40vw, 220px)'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-3px)';
        e.target.style.boxShadow = '0 6px 20px rgba(44,82,130,0.4)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 4px 15px rgba(44,82,130,0.3)';
      }}
    >
      ➕ Add New Car
    </button>
    
    <button
      onClick={() => window.location.href = '/admin-lease-to-own-documents'}
      style={{
        background: '#2c5282',
        color: 'white',
        padding: 'clamp(0.8rem, 2vw, 1rem) clamp(1.5rem, 4vw, 3rem)',
        border: 'none',
        borderRadius: '8px',
        fontSize: 'clamp(0.95rem, 2vw, 1.2rem)',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(44,82,130,0.3)',
        transition: 'all 0.3s ease',
        minWidth: 'clamp(180px, 40vw, 220px)'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-3px)';
        e.target.style.boxShadow = '0 6px 20px rgba(44,82,130,0.4)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 4px 15px rgba(44,82,130,0.3)';
      }}
    >
      📋 Add Notes + Agreement
    </button>
  </div>
</div>
        {showCarForm && (
          <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', marginBottom: '4rem', maxWidth: '800px', margin: '0 auto 4rem' }}>
            <h2 style={{ textAlign: 'center', color: '#2c5282', marginBottom: '2rem', fontSize: '2rem' }}>
              Add New Car
            </h2>

            {formMessage && (
              <div style={{
                padding: '1rem',
                marginBottom: '1.5rem',
                borderRadius: '8px',
                background: formMessage.startsWith('✅') ? '#d4edda' : '#f8d7da',
                color: formMessage.startsWith('✅') ? '#155724' : '#721c24',
                textAlign: 'center'
              }}>
                {formMessage}
              </div>
            )}

            <form onSubmit={handleCarSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mode</label>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <label>
                    <input type="radio" checked={useExistingCar} onChange={() => setUseExistingCar(true)} />
                    Select from Catalog
                  </label>
                  <label>
                    <input type="radio" checked={!useExistingCar} onChange={() => setUseExistingCar(false)} />
                    Create New
                  </label>
                </div>
              </div>

              {useExistingCar ? (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Select Vehicle from Catalog *</label>
                  <select
                    value={carFormData.originalVehicleId}
                    onChange={handleExistingCarChange}
                    required
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                  >
                    <option value="">-- Choose a vehicle --</option>
                    {originalVehicles.map(v => (
                      <option key={v._id} value={v._id}>
                        {v.name} ({v.status})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Car Name *</label>
                    <input
                      type="text"
                      name="carName"
                      value={carFormData.carName}
                      onChange={handleCarFormChange}
                      required
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Image *</label>
                    <input type="file" accept="image/*" onChange={handleImageChange} required />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Brochure (PDF - Optional)</label>
                    <input type="file" accept=".pdf" onChange={handleBrochureChange} />
                  </div>
                </>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Selling Price (Nu.) - Optional</label>
                <input
                  type="number"
                  name="sellingPrice"
                  value={carFormData.sellingPrice}
                  onChange={handleCarFormChange}
                  placeholder="e.g. 5000000"
                  style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>Leave blank if not applicable</small>
              </div>

              {imagePreview && (
                <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '1rem 3rem',
                    background: '#2c5282',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    cursor: formLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {formLoading ? 'Saving...' : 'Add Car'}
                </button>
                <button
                  type="button"
                  onClick={resetCarForm}
                  style={{
                    padding: '1rem 3rem',
                    background: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {leaseVehicles.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '2rem' }}>
            {leaseVehicles.map((car) => (
              <div
                key={car._id}
                onMouseEnter={() => setHoveredCard(car._id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: hoveredCard === car._id ? '0 20px 40px rgba(0,0,0,0.15)' : '0 8px 25px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s ease',
                  transform: hoveredCard === car._id ? 'translateY(-10px)' : 'translateY(0)'
                }}
              >
                <div style={{ height: '280px', overflow: 'hidden' }}>
                  <img
                    src={car.imageURL}
                    alt={car.carName}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s ease',
                      transform: hoveredCard === car._id ? 'scale(1.1)' : 'scale(1)'
                    }}
                  />
                </div>

                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.6rem', fontWeight: '600', marginBottom: '0.5rem' }}>{car.carName}</h3>
                  
                  {car.sellingPrice && (
                    <p style={{ color: '#2c5282', fontWeight: '600', fontSize: '1.2rem', marginBottom: '1rem' }}>
                      Nu. {formatPrice(car.sellingPrice)}
                    </p>
                  )}

                  {car.brochureURL && (
                    <a 
                      href={car.brochureURL} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ 
                        display: 'inline-block',
                        marginBottom: '1rem',
                        color: '#2c5282',
                        textDecoration: 'underline'
                      }}
                    >
                      📄 View Brochure
                    </a>
                  )}

                  <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                    <h4 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                      Payment Plans ({car.paymentPlans?.length || 0})
                    </h4>

                    {car.paymentPlans && car.paymentPlans.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {car.paymentPlans.map((plan) => (
                          <div key={plan._id} style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #ddd' }}>
                            <div style={{ marginBottom: '0.8rem' }}>
                              <strong style={{ fontSize: '1.1rem', color: '#2c5282' }}>{plan.optionTitle}</strong>
                            </div>

                            <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: '1.6', marginBottom: '0.8rem' }}>
                              {plan.option1Name && <p><strong>{plan.option1Name}:</strong> {plan.option1Value}</p>}
                              {plan.option2Name && <p><strong>{plan.option2Name}:</strong> {plan.option2Value}</p>}
                              {plan.option3Name && <p><strong>{plan.option3Name}:</strong> {plan.option3Value}</p>}
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button 
                                onClick={() => openEditPlanModal(car, plan)}
                                style={{ 
                                  flex: 1, 
                                  padding: '0.5rem', 
                                  background: '#3182ce', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => deletePlan(car._id, plan._id)}
                                style={{ 
                                  flex: 1, 
                                  padding: '0.5rem', 
                                  background: '#e53e3e', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                        No payment plans yet
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginTop: '1.5rem' }}>
                    <button 
                      onClick={() => openPlanForm(car)} 
                      style={{ 
                        padding: '0.8rem', 
                        background: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      + Create Plan
                    </button>
                    <button 
                      onClick={() => handleDeleteCar(car)} 
                      style={{ 
                        padding: '0.8rem', 
                        background: '#e53e3e', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Delete Car
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {leaseVehicles.length === 0 && !showCarForm && (
          <div style={{ textAlign: 'center', padding: '8rem 2rem', color: '#888' }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#444' }}>No cars added yet</h2>
            <p style={{ fontSize: '1.3rem' }}>Click the button above to add your first lease-to-own car</p>
          </div>
        )}

        {showPlanForm && selectedCar && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#2c5282', textAlign: 'center', fontSize: '1.8rem' }}>
                Create Payment Plan for {selectedCar.carName}
              </h3>

              <form onSubmit={handlePlanSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option Title *</label>
                  <input 
                    type="text" 
                    name="optionTitle" 
                    value={planFormData.optionTitle} 
                    onChange={handlePlanFormChange} 
                    required 
                    placeholder="e.g. 36 Months Plan"
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Name</label>
                    <input 
                      type="text" 
                      name="option1Name" 
                      value={planFormData.option1Name} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Down Payment"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Value</label>
                    <input 
                      type="text" 
                      name="option1Value" 
                      value={planFormData.option1Value} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Nu. 500,000"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Name</label>
                    <input 
                      type="text" 
                      name="option2Name" 
                      value={planFormData.option2Name} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Monthly Payment"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Value</label>
                    <input 
                      type="text" 
                      name="option2Value" 
                      value={planFormData.option2Value} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Nu. 50,000"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Name</label>
                    <input 
                      type="text" 
                      name="option3Name" 
                      value={planFormData.option3Name} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. Tenure"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Value</label>
                    <input 
                      type="text" 
                      name="option3Value" 
                      value={planFormData.option3Value} 
                      onChange={handlePlanFormChange} 
                      placeholder="e.g. 36 months"
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button 
                    type="submit" 
                    disabled={formLoading}
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#2c5282', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: formLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {formLoading ? 'Saving...' : 'Add Plan'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowPlanForm(false)} 
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#777', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditPlanModal && editingPlan && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#2c5282', textAlign: 'center', fontSize: '1.8rem' }}>
                Edit Payment Plan
              </h3>

              <form onSubmit={handleEditPlanSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option Title *</label>
                  <input 
                    type="text" 
                    name="optionTitle" 
                    value={planFormData.optionTitle} 
                    onChange={handlePlanFormChange} 
                    required 
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Name</label>
                    <input 
                      type="text" 
                      name="option1Name" 
                      value={planFormData.option1Name} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 1 Value</label>
                    <input 
                      type="text" 
                      name="option1Value" 
                      value={planFormData.option1Value} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Name</label>
                    <input 
                      type="text" 
                      name="option2Name" 
                      value={planFormData.option2Name} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 2 Value</label>
                    <input 
                      type="text" 
                      name="option2Value" 
                      value={planFormData.option2Value} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Name</label>
                    <input 
                      type="text" 
                      name="option3Name" 
                      value={planFormData.option3Name} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Option 3 Value</label>
                    <input 
                      type="text" 
                      name="option3Value" 
                      value={planFormData.option3Value} 
                      onChange={handlePlanFormChange} 
                      style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button 
                    type="submit" 
                    disabled={formLoading}
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#2c5282', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: formLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {formLoading ? 'Saving...' : 'Update Plan'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowEditPlanModal(false)} 
                    style={{ 
                      flex: 1, 
                      padding: '1rem', 
                      background: '#777', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeaseToOwnList;